import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, ArrowUpRight, Smartphone, Zap, PackageOpen, CheckCircle2,
  ShieldCheck, Ticket, Timer, Sparkles, Bot, Store, MapPin, Leaf,
} from "lucide-react";
import PageHeader from "../components/PageHeader";
import useReveal from "../hooks/useReveal";
import { brand, navPaths, risoriceImages, risoriceMenu } from "../mock/mock";
import { useLang } from "../i18n/LanguageContext";
import Seo from "../components/Seo";
import MapSedi from "../components/MapSedi";
import FranchiseSim from "../components/FranchiseSim";

const howIcons = [Smartphone, Zap, PackageOpen];
const advIcons = [ShieldCheck, Ticket, Timer, Sparkles, Bot, Store];

export default function Risorice() {
  const { t, lang } = useLang();
  useReveal([t]);
  const r = t.risorice;

  return (
    <main>
      <Seo
        title="RISORICE® | Ristorante gluten-free automatizzato in franchising"
        description="RISORICE®: cucina italiana 100% gluten-free cotta in 5 minuti con MAGNETRON e ritirata dal Food-Box Wall. Scopri il franchising e il simulatore."
        path="/risorice"
        image={risoriceImages.store}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Restaurant",
          name: "RISORICE®",
          servesCuisine: ["Italian", "Gluten-free"],
          priceRange: "€",
          url: "https://www.michelchef.it/risorice",
          image: risoriceImages.store,
          description: "Ristorante italiano 100% gluten-free automatizzato: cottura MAGNETRON in 5 minuti e ritiro dal Food-Box Wall. Franchising disponibile.",
          parentOrganization: { "@type": "Organization", name: "MichelChef® S.r.l." },
        }}
      />
      <PageHeader crumb={r.crumb} eyebrow={r.eyebrow} title={r.title} subtitle={r.subtitle} image={risoriceImages.store} homeLabel={t.common.home} />

      {/* Quick actions */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 -mt-8 relative z-10">
        <div className="reveal flex flex-wrap gap-4 justify-center">
          <a href={brand.risoRiceUrl} target="_blank" rel="noreferrer" className="btn-press inline-flex items-center gap-2 rounded-full bg-[#22B573] text-[#083a24] px-7 py-3.5 font-semibold hover:bg-[#1ba065] shadow-lg">
            {r.officialBtn} <ArrowUpRight size={18} />
          </a>
          <Link to={navPaths.contact} className="btn-press inline-flex items-center gap-2 rounded-full bg-[#14110F] text-white px-7 py-3.5 font-semibold hover:bg-[#2a251f]">
            {r.contactBtn} <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      {/* STATS */}
      <section className="py-16 max-w-7xl mx-auto px-5 lg:px-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          {r.stats.map((s, i) => (
            <div key={i} className={`reveal reveal-delay-${i} bg-white rounded-2xl border border-black/5 shadow-sm p-6 text-center`}>
              <div className="font-display font-extrabold text-3xl lg:text-4xl text-[#17784A]">{s.value}</div>
              <div className="mt-2 text-[#3B342E]/70 text-sm">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-[#F3EFE7] py-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="reveal text-center max-w-2xl mx-auto">
            <span className="text-[#D8382E] font-semibold text-sm uppercase tracking-wider">Come funziona</span>
            <h2 className="font-display font-extrabold text-3xl lg:text-4xl mt-3 text-[#14110F]">{r.howTitle}</h2>
          </div>
          <div className="mt-14 grid md:grid-cols-3 gap-6">
            {r.how.map((s, i) => {
              const Icon = howIcons[i];
              return (
                <div key={i} className={`reveal reveal-delay-${i} bg-white rounded-2xl p-8 shadow-sm border border-black/5 card-lift`}>
                  <div className="flex items-center justify-between">
                    <span className="grid place-items-center w-12 h-12 rounded-xl bg-[#17784A] text-white"><Icon size={24} /></span>
                    <span className="font-display font-extrabold text-3xl text-[#22B573]/30">{s.n}</span>
                  </div>
                  <h3 className="font-display font-bold text-lg mt-5 text-[#14110F]">{s.t}</h3>
                  <p className="mt-2 text-[#3B342E]/75 leading-relaxed">{s.d}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* TECH MAGNETRON */}
      <section className="py-24 max-w-7xl mx-auto px-5 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <div className="reveal">
            <span className="inline-flex items-center gap-2 rounded-full bg-[#17784A]/10 text-[#17784A] px-4 py-1.5 text-sm font-semibold"><Zap size={15} /> MICHELCHEF®</span>
            <h2 className="font-display font-extrabold text-3xl lg:text-4xl mt-4 text-[#14110F]">{r.techTitle}</h2>
            <p className="mt-6 text-[#3B342E]/90 text-lg leading-relaxed">{r.techText}</p>
            <ul className="mt-6 space-y-3">
              {r.techPoints.map((p, i) => (
                <li key={i} className="flex items-start gap-3 text-[#3B342E]/85">
                  <CheckCircle2 size={20} className="text-[#17784A] mt-0.5 shrink-0" /> {p}
                </li>
              ))}
            </ul>
          </div>
          <div className="reveal reveal-delay-1 bg-[#14110F] rounded-3xl p-8 flex items-center justify-center">
            <img src={risoriceImages.magnetron} alt="MAGNETRON" className="w-full max-h-[380px] object-contain" />
          </div>
        </div>
      </section>

      {/* FOOD-BOX WALL */}
      <section className="py-8 pb-24 max-w-7xl mx-auto px-5 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <div className="reveal order-2 lg:order-1">
            <img src={risoriceImages.wall} alt="Food-Box Wall" className="rounded-3xl w-full h-[420px] object-cover shadow-2xl" />
          </div>
          <div className="reveal reveal-delay-1 order-1 lg:order-2">
            <span className="inline-flex items-center gap-2 rounded-full bg-[#D8382E]/10 text-[#D8382E] px-4 py-1.5 text-sm font-semibold"><PackageOpen size={15} /> Food-Box Wall</span>
            <h2 className="font-display font-extrabold text-3xl lg:text-4xl mt-4 text-[#14110F]">{r.wallTitle}</h2>
            <p className="mt-6 text-[#3B342E]/90 text-lg leading-relaxed">{r.wallText}</p>
          </div>
        </div>
      </section>

      {/* ADVANTAGES */}
      <section className="bg-[#F3EFE7] py-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="reveal text-center max-w-2xl mx-auto">
            <span className="text-[#17784A] font-semibold text-sm uppercase tracking-wider">Vantaggi</span>
            <h2 className="font-display font-extrabold text-3xl lg:text-4xl mt-3 text-[#14110F]">{r.advTitle}</h2>
          </div>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {r.advantages.map((a, i) => {
              const Icon = advIcons[i];
              return (
                <div key={i} className={`reveal reveal-delay-${i} bg-white rounded-2xl p-7 shadow-sm border border-black/5 card-lift hover:shadow-xl`}>
                  <span className="grid place-items-center w-12 h-12 rounded-xl bg-[#17784A]/10 text-[#17784A]"><Icon size={24} /></span>
                  <h3 className="font-display font-bold text-lg mt-5 text-[#14110F]">{a.t}</h3>
                  <p className="mt-2 text-[#3B342E]/75 leading-relaxed">{a.d}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* STORES + MAP */}
      <section className="py-24 max-w-7xl mx-auto px-5 lg:px-8">
        <div className="reveal text-center max-w-2xl mx-auto">
          <span className="text-[#D8382E] font-semibold text-sm uppercase tracking-wider">{r.storesEyebrow}</span>
          <h2 className="font-display font-extrabold text-3xl lg:text-4xl mt-3 text-[#14110F]">{r.storesTitle}</h2>
        </div>
        <div className="mt-12 grid lg:grid-cols-2 gap-8 items-start">
          <div className="reveal space-y-4">
            {r.stores.map((s, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 shadow-sm border border-black/5 card-lift flex gap-4">
                <span className="grid place-items-center w-12 h-12 rounded-xl bg-[#17784A]/10 text-[#17784A] shrink-0"><MapPin size={22} /></span>
                <div className="flex-1">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <h3 className="font-display font-bold text-lg text-[#14110F]">{s.city}</h3>
                    <span className="rounded-full bg-[#22B573]/15 text-[#0f7a4a] px-3 py-1 text-xs font-semibold">{s.opening}</span>
                  </div>
                  <p className="mt-1 text-[#3B342E]/80">{s.address}</p>
                  <p className="mt-1 text-sm text-[#3B342E]/55">{s.service}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="reveal reveal-delay-1">
            <MapSedi locations={r.stores.map((s) => ({ type: s.city, address: s.address, city: s.opening, lat: s.lat, lng: s.lng }))} />
          </div>
        </div>
      </section>

      {/* MENU */}
      <section className="bg-[#F3EFE7] py-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="reveal text-center max-w-2xl mx-auto">
            <span className="text-[#17784A] font-semibold text-sm uppercase tracking-wider">{r.menuEyebrow}</span>
            <h2 className="font-display font-extrabold text-3xl lg:text-4xl mt-3 text-[#14110F]">{r.menuTitle}</h2>
            <p className="mt-4 text-[#3B342E]/70">{r.menuNote}</p>
          </div>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {risoriceMenu.map((m, i) => (
              <div key={i} className="reveal group bg-white rounded-2xl overflow-hidden shadow-sm border border-black/5 card-lift hover:shadow-xl">
                <div className="relative h-44 overflow-hidden">
                  <img src={m.img} alt={m.name} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <span className="absolute top-3 left-3 inline-flex items-center gap-1 rounded-full bg-[#17784A] text-white px-2.5 py-1 text-xs font-semibold shadow">
                    <Leaf size={12} /> {r.glutenBadge}
                  </span>
                </div>
                <div className="p-5">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-display font-bold text-[#14110F] leading-snug">{m.name}</h3>
                    <span className="font-display font-extrabold text-[#D8382E] whitespace-nowrap">€ {m.price}</span>
                  </div>
                  <p className="mt-2 text-sm text-[#3B342E]/70 leading-relaxed">{m.desc[lang]}</p>
                  <a href={brand.risoRiceUrl} target="_blank" rel="noreferrer" className="btn-press mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#17784A] text-white px-4 py-2.5 text-sm font-semibold hover:bg-[#13633c]">
                    {r.orderBtn} <ArrowUpRight size={15} />
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FRANCHISE SIMULATOR */}
      <FranchiseSim />

      {/* CTA */}
      <section className="py-24 max-w-7xl mx-auto px-5 lg:px-8">
        <div className="reveal relative overflow-hidden rounded-[32px] bg-[#17784A] px-8 py-16 lg:p-20 grain">
          <div className="relative max-w-2xl">
            <h2 className="font-display font-extrabold text-white text-3xl lg:text-5xl tracking-tight">{r.ctaTitle}</h2>
            <p className="mt-5 text-white/80 text-lg">{r.ctaText}</p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to={navPaths.contact} className="btn-press inline-flex items-center gap-2 rounded-full bg-white text-[#14110F] px-7 py-3.5 font-semibold hover:bg-[#FBF9F4]">
                {r.ctaBtn} <ArrowRight size={18} />
              </Link>
              <a href={brand.risoRiceUrl} target="_blank" rel="noreferrer" className="btn-press inline-flex items-center gap-2 rounded-full border border-white/40 text-white px-7 py-3.5 font-semibold hover:bg-white/10">
                {r.officialBtn} <ArrowUpRight size={18} />
              </a>
            </div>
          </div>
          <PackageOpen size={260} className="absolute -right-10 -bottom-14 text-white/5" />
        </div>
      </section>
    </main>
  );
}
